/* prettier-ignore */

import { inject, Injectable, NgZone } from '@angular/core';
import { ScrollService } from './scrollContainer.service';
import { DataChatService } from './dataChat.service';
import { Subject } from 'rxjs';
import { AssistantToolsHandlerService } from './assistantToolsHandler.service';

type TypingState = {
  fullText: string;
  index: number;
  timerId?: number;
};

@Injectable({ providedIn: 'root' })

export class ChatBotTextAnimationService {
  private ngZone = inject(NgZone);
  private dataChat = inject(DataChatService);
  private scrollService = inject(ScrollService);
  private assistantToolsHandlerService = inject(AssistantToolsHandlerService);

  private hiddenCommandsMap = new Map<string, string>();
  private typing = new Map<string, TypingState>();

  private baseDelay = 36;
  private punctuationDelay = 160;
  private charsPerTick = 2;
  private punctuationRegex = /[.,;:!?…]/;

  private completedMessages = new Set<string>();
  private pendingProactive = new Map<string, { text: string; hiddenCommand?: string }>();
  private cdScheduled = false;

  private allIdleSubject = new Subject<void>();
  public allIdle$ = this.allIdleSubject.asObservable();

  enqueueText(id: string, text: string, hiddenCommand?: string) {
    if (!text) 
      return;

    const safeText = this.assistantToolsHandlerService.processAssistantTools(id, text);
    if (!safeText) 
      return;

    if (hiddenCommand)
      this.hiddenCommandsMap.set(id, hiddenCommand);

    const isProactive = id.includes('_tip');
    if (isProactive) {
      const parentId = this.getParentIdFromTip(id);
      if (!this.completedMessages.has(parentId)) {
        const pending = this.pendingProactive.get(id);
        if (pending) {
          pending.text += safeText;
          if (hiddenCommand) pending.hiddenCommand = hiddenCommand;
        } else {
          this.pendingProactive.set(id, { text: safeText, hiddenCommand });
        }
        return;
      }
    }

    const running = this.typing.get(id);
    if (running) {
      running.fullText += safeText;
      return;
    }

    this.startTyping(id, safeText);
  }

  private startTyping(id: string, fullText: string) {
    this.cancel(id, false);
    this.typing.set(id, { fullText, index: 0 });

    const isProactive = id.includes('_tip');
    if (!isProactive) 
      this.ensureMessageExists(id);

    this.tick(id);
  }

  cancel(id: string, revealFullText = true) {
    const state = this.typing.get(id);
    if (!state) 
      return;

    if (state.timerId != null) 
      clearTimeout(state.timerId);

    if (revealFullText) {
      this.setAssistantMessageContent(id, state.fullText);
      this.scrollService.scrollToBottom()
    }

    this.typing.delete(id);
  }

  private tick(id: string) {
    const state = this.typing.get(id);
    if (!state) 
      return;

    if (state.index >= state.fullText.length) {
      this.typing.delete(id);
      this.completedMessages.add(id);
      this.scheduleChangeDetection();
      this.flushPendingProactives(id);
      return;
    }

    const prevIndex = state.index;
    const nextIndex = Math.min(state.index + this.charsPerTick, state.fullText.length);
    state.index = nextIndex;

    const chunkToAppend = state.fullText.slice(prevIndex, nextIndex);
    if (chunkToAppend)
      this.handleAssistenQueueMessage(id, chunkToAppend);

    const lastChar = state.fullText.slice(0, state.index).trimEnd().slice(-1);
    const delay = this.punctuationRegex.test(lastChar)
      ? this.punctuationDelay
      : this.baseDelay;

    this.scheduleChangeDetection();
    this.ngZone.runOutsideAngular(() => {
      state.timerId = window.setTimeout(() => this.tick(id), delay);
    });
  }

  private scheduleChangeDetection(): void {
    if (this.cdScheduled) return;
    this.cdScheduled = true;
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.cdScheduled = false;
        this.ngZone.run(() => {});
        this.scrollService.scrollToBottom()
      });
    });
  }

  private flushPendingProactives(parentId: string) {
    for (const [tipId, pending] of this.pendingProactive.entries()) {
      if (this.getParentIdFromTip(tipId) === parentId) {
        this.pendingProactive.delete(tipId);

        if (pending.hiddenCommand) 
          this.hiddenCommandsMap.set(tipId, pending.hiddenCommand);

        this.startTyping(tipId, pending.text);
      }
    }
  }

  private getParentIdFromTip(id: string): string {
    return id?.split('_tip')?.[0];
  }

  private ensureMessageExists(id: string) {
    this.dataChat.messages.update((prev) => {
      const exists = prev.some((m: any) => m.id === id);
      if (exists) 
        return prev;

      return [
        ...prev,
        {
          id,
          role: 'assistant',
          content: '',
          show: true,
        },
      ];
    });
  }

  private setAssistantMessageContent(id: string, content: string) {
    this.dataChat.messages.update((prev) => {
      const index = prev.findIndex((m: any) => m.id === id);
      if (index === -1) 
        return prev;

      const updated = [...prev];
      updated[index] = { ...updated[index], content };
      return updated;
    });
  }

  createUserMessage(message: string) {
    if (!message || !message.trim()) 
      return;

    this.dataChat.messages.update((prev) => {
      // Evitar duplicados exactos si se llama dos veces seguidas (ej: voz + websocket)
      const lastMsg = prev.length > 0 ? prev[prev.length - 1] : null;
      if (lastMsg && lastMsg.role === 'user' && lastMsg.content === message) {
        return prev;
      }
      return [...prev, { role: 'user', content: message, show: true }];
    });

    this.scrollService.scrollToBottom()
  }

  handleAssistenQueueMessage(id: string, text: string) {
    this.dataChat.messages.update((prev) => {
      const index = prev.findIndex((m: any) => m.id === id);
      const isProactive = id.includes('_tip');
      const hiddenCommand = this.hiddenCommandsMap.get(id);
      if (hiddenCommand) 
        this.hiddenCommandsMap.delete(id);

      if (index !== -1) {
        // Mensaje ya existe → actualizar contenido (chunk append)
        const updated = [...prev];
        const target = updated[index];

        if (isProactive) {
          // Actualizar el proactiveChildren del padre
          const parentIdx = this._findParentBotIndex(updated, index);
          if (parentIdx !== -1) {
            const parent = updated[parentIdx];
            const children = [...(parent.proactiveChildren || [])];
            const childIdx = children.findIndex(c => c.id === id);
            if (childIdx !== -1) {
              children[childIdx] = {
                ...children[childIdx],
                content: (children[childIdx].content ?? '') + text,
                hiddenCommand: hiddenCommand || children[childIdx].hiddenCommand
              };
            }
            updated[parentIdx] = { ...parent, proactiveChildren: children };
          }
        }

        updated[index] = {
          ...target,
          content: (target.content ?? '') + text,
          isProactive,
          hiddenCommand: hiddenCommand || target.hiddenCommand
        };
        return updated;
      }

      // Mensaje nuevo
      const newMsg: any = { role: 'assistant', content: text, show: true, id, isProactive, hiddenCommand };

      if (isProactive) {
        // Adjuntar al último mensaje bot como proactiveChildren[] y ocultar el standalone
        const updated = [...prev];
        const parentIdx = this._findLastBotIndex(updated);
        if (parentIdx !== -1) {
          const parent = updated[parentIdx];
          const children = [...(parent.proactiveChildren || [])];
          children.push({ ...newMsg });
          updated[parentIdx] = { ...parent, proactiveChildren: children };
          // Añadir al array con show:false (oculto, solo para tracking de chunks)
          return [...updated, { ...newMsg, show: false }];
        }
      }

      return [...prev, newMsg];
    });
  }

  private _findParentBotIndex(messages: any[], proactiveIdx: number): number {
    for (let i = proactiveIdx - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].isProactive) return i;
    }
    return -1;
  }

  private _findLastBotIndex(messages: any[]): number {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && !messages[i].isProactive) return i;
    }
    return -1;
  }

}